<template>
  <b-modal
    id="franchise-manual-cycle-modal"
    v-model="isVisible"
    centered
    size="lg"
    :hide-header-close="isBlocking"
    :no-close-on-backdrop="isBlocking"
    :no-close-on-esc="isBlocking"
  >
    <template #modal-title>
      <span class="font-weight-bold">{{ title }}</span>
    </template>

    <p>{{ body }}</p>

    <ul class="list-unstyled">
      <li v-for="item in items" :key="item.manual_id" class="mb-50">
        <feather-icon icon="FileTextIcon" class="mr-50" />
        <a :href="item.file_url" target="_blank" rel="noopener">
          {{ item.name }} — versão {{ item.version }}
        </a>
        <b-badge v-if="item.is_mandatory" variant="light-danger" class="ml-50">Obrigatório</b-badge>
      </li>
    </ul>

    <template v-if="step === 'reading'">
      <hr />
      <p class="font-weight-bold mb-50">Documentos abrangidos por esta confirmação:</p>
      <ul class="mb-1">
        <li v-for="item in items" :key="`accept-${item.manual_id}`">
          {{ item.name }} — versão {{ item.version }}
        </li>
      </ul>
      <b-form-checkbox v-model="readingAcknowledged">
        Declaro que li e estou ciente do conteúdo dos Manuais do Franqueado disponibilizados pela Futuro Corp.
      </b-form-checkbox>
    </template>

    <template #modal-footer>
      <b-button v-if="!isBlocking" variant="outline-secondary" @click="isVisible = false">
        Continuar para o Oráculo
      </b-button>
      <b-button
        v-if="step === 'receipt'"
        variant="primary"
        :disabled="saving"
        @click="onConfirmReceipt"
      >
        {{ saving ? "Confirmando..." : "Confirmo que recebi e tive acesso aos manuais" }}
      </b-button>
      <b-button
        v-else
        variant="primary"
        :disabled="saving || !readingAcknowledged"
        @click="onConfirmReading"
      >
        {{ saving ? "Confirmando..." : "Confirmar leitura" }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BButton, BFormCheckbox, BBadge } from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import * as types from '../store/types'

const BLOCKING_STATUSES = ['blocked', 'expired']

export default {
  name: 'FranchiseManualCycleModal',
  components: { BModal, BButton, BFormCheckbox, BBadge },
  data() {
    return {
      isVisible: false,
      saving: false,
      readingAcknowledged: false,
    }
  },
  setup() {
    return { toast: useToast() }
  },
  computed: {
    ...mapGetters({
      cycle: types.MY_FRANCHISE_MANUAL_CYCLE,
    }),
    items() {
      return this.cycle?.items || []
    },
    step() {
      return this.cycle && !this.cycle.receipt_confirmed_at ? 'receipt' : 'reading'
    },
    isBlocking() {
      return !!(this.cycle && BLOCKING_STATUSES.includes(this.cycle.status))
    },
    shouldShow() {
      if (!this.cycle) return false
      return ['awaiting_receipt', 'pending_reading', 'near_deadline', 'expired', 'blocked'].includes(this.cycle.status)
    },
    title() {
      if (this.isBlocking) {
        return 'Acesso restrito — Manuais do Franqueado'
      }
      if (this.step === 'receipt') {
        return 'Manuais do Franqueado disponíveis'
      }
      return 'Confirme a leitura dos Manuais do Franqueado'
    },
    body() {
      if (this.isBlocking) {
        return 'Seu prazo para confirmação de leitura dos Manuais do Franqueado foi encerrado. '
          + 'Para continuar utilizando o Oráculo, conclua a leitura e confirme abaixo.'
      }
      if (this.step === 'receipt') {
        return 'Confirme que recebeu e teve acesso aos manuais abaixo, disponibilizados pela Futuro Corp.'
      }
      const days = this.cycle?.days_remaining
      if (days === null || days === undefined) {
        return 'Revise os manuais abaixo e confirme a leitura obrigatória.'
      }
      return `Você possui ${days} dia${days === 1 ? '' : 's'} para concluir e confirmar a leitura obrigatória.`
    },
  },
  watch: {
    shouldShow: {
      immediate: true,
      handler(val) {
        this.isVisible = val
      },
    },
  },
  created() {
    this.fetchMyCycle().catch(() => {})
  },
  methods: {
    ...mapActions({
      fetchMyCycle: types.GET_MY_FRANCHISE_MANUAL_CYCLE,
      confirmReceipt: types.CONFIRM_FRANCHISE_MANUAL_RECEIPT,
      confirmReading: types.CONFIRM_FRANCHISE_MANUAL_READING,
    }),
    onConfirmReceipt() {
      this.saving = true
      this.confirmReceipt()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao confirmar o recebimento. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
    onConfirmReading() {
      this.saving = true
      this.confirmReading()
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso',
              text: 'Leitura dos Manuais do Franqueado confirmada!',
              icon: 'CoffeeIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao confirmar a leitura. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>
